import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  /** Delay before showing, in ms. Keyboard focus always shows immediately. */
  delayMs?: number;
  /** Classes for the tooltip bubble. */
  className?: string;
  /** Classes for the anchor wrapper — e.g. `w-full` around a nav row. */
  wrapperClassName?: string;
  /** Force-disable, e.g. when a sidebar is expanded and labels are visible. */
  disabled?: boolean;
}

/**
 * Centering is done with framer's own transform values (`style.x` / `style.y`)
 * rather than Tailwind translate classes — an animated transform would
 * otherwise overwrite the class and knock the tooltip off centre.
 */
const POSITION: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 mb-2',
  bottom: 'top-full left-1/2 mt-2',
  left: 'right-full top-1/2 mr-2',
  right: 'left-full top-1/2 ml-2',
};

const CENTERING: Record<TooltipSide, { x?: string; y?: string }> = {
  top: { x: '-50%' },
  bottom: { x: '-50%' },
  left: { y: '-50%' },
  right: { y: '-50%' },
};

/** Entrance offset, always on the axis the tooltip is not centred on. */
const OFFSET: Record<TooltipSide, { x?: number; y?: number }> = {
  top: { y: 4 },
  bottom: { y: -4 },
  left: { x: 4 },
  right: { x: -4 },
};

/** Hover/focus tooltip. Keep the content short — it is not a popover. */
export function Tooltip({
  content,
  children,
  side = 'top',
  delayMs = 250,
  className,
  wrapperClassName,
  disabled,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  const id = useId();

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  if (!content || disabled) return <>{children}</>;

  const show = (immediate: boolean) => {
    window.clearTimeout(timerRef.current);
    if (immediate || delayMs <= 0) setOpen(true);
    else timerRef.current = window.setTimeout(() => setOpen(true), delayMs);
  };

  const hide = () => {
    window.clearTimeout(timerRef.current);
    setOpen(false);
  };

  return (
    <span
      className={cn('relative inline-flex', wrapperClassName)}
      onMouseEnter={() => show(false)}
      onMouseLeave={hide}
      onFocus={() => show(true)}
      onBlur={hide}
    >
      <span aria-describedby={open ? id : undefined} className="contents">
        {children}
      </span>
      <AnimatePresence>
        {open && (
          <motion.span
            id={id}
            role="tooltip"
            style={CENTERING[side]}
            initial={{ opacity: 0, ...OFFSET[side] }}
            animate={{ opacity: 1, ...(side === 'top' || side === 'bottom' ? { y: 0 } : { x: 0 }) }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className={cn(
              'pointer-events-none absolute z-50 w-max max-w-64 rounded-md border border-border',
              'bg-elevated px-2 py-1 text-xs font-medium text-fg shadow-pop',
              POSITION[side],
              className,
            )}
          >
            {content}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
